import { NavLink } from 'react-router-dom';
import type { FeatureFlags, MeResponse } from '@ucp/web/lib';
import { cn } from '../ui/cn';
import { visibleTasks, type ModuleSubModule } from './modulesConfig';
import { NavMenuIconLabel } from './NavMenuIconLabel';
import { iconForTask } from './navIcons';
import { ScrollNavBar, taskNavListClassName } from './ScrollNavBar';

/**
 * Tier-2 task navigation for a nested-module sub-capability (ADR-014 Amendment 3).
 * Mirrors `PrimaryNav` styling; `ScrollNavBar` is the sole tier-2 `<nav>` landmark (NF-10).
 * Items wrap onto extra rows (ADR-014 Amendment 18).
 * The landmark is named after the **sub-module** (e.g. "Complete Appraisal"), not the parent module.
 *
 * PRO-1290 — `flags` filters tasks that carry their own `requiresFlag`. Optional so every
 * shipped call site and test keeps today's behaviour; omitting it hides flag-gated tasks,
 * which is the safe default.
 *
 * PRO-2010 — `user.hrCaseTrack` shows "My HR cases"; absent hides it.
 */
export function SubModuleTaskNav({
  subModule,
  flags = {},
  user,
}: {
  subModule: ModuleSubModule;
  flags?: Partial<FeatureFlags>;
  user?: Pick<MeResponse, 'roles'> & Partial<Pick<MeResponse, 'workforceBaseline' | 'hrCaseTrack'>>;
}) {
  const items = visibleTasks(subModule, flags, user, { hrCaseTrack: user?.hrCaseTrack === true });

  return (
    <ScrollNavBar aria-label={subModule.label}>
      <ul className={cn(taskNavListClassName, 'py-2')}>
        {items.map((item) => (
          <li key={item.id}>
            <NavLink
              to={item.path}
              end={item.end ?? false}
              className={({ isActive }) =>
                cn(
                  'inline-flex min-h-11 items-center whitespace-nowrap px-3 py-2 text-base no-underline transition-colors ease-ds',
                  'border-b-2 border-transparent hover:bg-muted',
                  isActive
                    ? 'border-primary font-semibold text-text-primary'
                    : 'font-normal text-text-secondary',
                )
              }
            >
              {({ isActive }) => (
                <NavMenuIconLabel
                  icon={iconForTask(item.id)}
                  active={isActive}
                  className="gap-2"
                  iconClassName="h-4 w-4"
                >
                  {item.label}
                </NavMenuIconLabel>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </ScrollNavBar>
  );
}
